import * as React from "react";
import { cn } from "@/v2/lib/utils";
import { dateFromKey, formatHHmm, programsFor, setTranslucentDragImage } from "../schedulerUtils";
import type { Channel, DragPayload, Program } from "../types";
import type { TouchDragHandle } from "../useTouchDragBridge";
import { useMediaQuery } from "@/v2/hooks/useMediaQuery";
import { ChannelLabel } from "./ChannelLabel";
import { colorStyle } from "./parts";

const LABEL_WIDTH = 152;
/** See TimelineGrid's LABEL_WIDTH_COMPACT: same icon-only collapse below sm. */
const LABEL_WIDTH_COMPACT = 64;
const DAY_MIN_WIDTH = 150;

interface Props {
  channels: Channel[];
  programs: Program[];
  dateKeys: string[];
  todayKey: string;
  selectedDate: string;
  selectedId: string | null;
  dragRef: React.MutableRefObject<DragPayload | null>;
  touchDrag?: TouchDragHandle;
  onSelect: (id: string) => void;
  onOpenDay: (date: string) => void;
  onDropOnDay: (target: { channelId: string; date: string }) => void;
}

/** Seven-day view of the same program data; drop a block on a cell to move it to that day. */
export function WeekGrid({
  channels,
  programs,
  dateKeys,
  todayKey,
  selectedDate,
  selectedId,
  dragRef,
  touchDrag,
  onSelect,
  onOpenDay,
  onDropOnDay,
}: Props) {
  const [hover, setHover] = React.useState<string | null>(null);
  const compact = useMediaQuery("(max-width: 639px)");
  const labelWidth = compact ? LABEL_WIDTH_COMPACT : LABEL_WIDTH;
  const columns = `${labelWidth}px repeat(7, minmax(${DAY_MIN_WIDTH}px, 1fr))`;

  return (
    <div className="overflow-x-auto" data-testid="week-grid">
      <div style={{ minWidth: labelWidth + DAY_MIN_WIDTH * 7 }}>
        <div className="grid border-b border-border" style={{ gridTemplateColumns: columns }}>
          <div className="sticky left-0 z-10 bg-card" />
          {dateKeys.map((key) => {
            const d = dateFromKey(key);
            return (
              <div
                key={key}
                className="text-center"
                style={{ padding: "14px 4px" }}
              >
                <button
                  type="button"
                  onClick={() => onOpenDay(key)}
                  className={cn(
                    "rounded-md text-xs font-medium hover:bg-accent",
                    key === todayKey ? "text-action-hover" : "text-muted-foreground",
                  )}
                  style={{ padding: "4px 8px" }}
                  aria-label={`Open ${d.toDateString()}`}
                >
                  <span className="block">{d.toLocaleDateString("en-US", { weekday: "short" })}</span>
                  <span
                    className={cn(
                      "block text-base font-semibold",
                      key === selectedDate ? "text-action-hover" : "text-foreground",
                    )}
                  >
                    {d.getDate()}
                  </span>
                </button>
              </div>
            );
          })}
        </div>

        {channels.map((channel) => (
          <div
            key={channel.id}
            className="grid border-b border-border last:border-b-0"
            style={{ gridTemplateColumns: columns }}
            data-testid={`week-row-${channel.id}`}
          >
            <div
              className="sticky left-0 z-10 flex items-center bg-card"
              style={{ padding: compact ? "0 10px" : "0 16px" }}
            >
              <ChannelLabel channel={channel} compact={compact} />
            </div>
            {dateKeys.map((key) => {
              const cellId = `${channel.id}:${key}`;
              return (
                <div
                  key={key}
                  data-testid={`week-cell-${cellId}`}
                  data-drop-target
                  className={cn(
                    "flex flex-col border-l border-border",
                    hover === cellId && "bg-action-light",
                  )}
                  style={{ gap: 4, padding: "8px 6px", minHeight: 120 }}
                  onDragOver={(e) => {
                    if (!dragRef.current) return;
                    e.preventDefault();
                    setHover(cellId);
                  }}
                  onDragLeave={() => setHover(null)}
                  onDrop={(e) => {
                    setHover(null);
                    if (!dragRef.current) return;
                    e.preventDefault();
                    onDropOnDay({ channelId: channel.id, date: key });
                  }}
                >
                  {programsFor(programs, channel.id, key).map((program) => (
                    <div
                      key={program.id}
                      role="button"
                      tabIndex={0}
                      draggable
                      data-testid={`week-program-${program.id}`}
                      onClick={() => onSelect(program.id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onSelect(program.id);
                        }
                      }}
                      onDragStart={(e) => {
                        dragRef.current = {
                          kind: "program",
                          id: program.id,
                          durationMin: 0,
                          grabOffsetMin: 0,
                        };
                        e.dataTransfer.effectAllowed = "move";
                        e.dataTransfer.setData("text/plain", program.id);
                        setTranslucentDragImage(e, e.currentTarget as HTMLElement);
                      }}
                      onDragEnd={() => (dragRef.current = null)}
                      {...touchDrag?.bind(
                        () => ({ kind: "program", id: program.id, durationMin: 0, grabOffsetMin: 0 }),
                        program.title,
                      )}
                      className={cn(
                        "cursor-grab overflow-hidden rounded border",
                        program.id === selectedId && "ring-2 ring-action",
                      )}
                      style={{ ...colorStyle(program.color), padding: "3px 6px" }}
                    >
                      <div className="truncate text-[11px] font-semibold leading-tight text-foreground">
                        {program.title}
                      </div>
                      <div className="text-[11px] leading-tight text-muted-foreground">
                        {formatHHmm(program.startTime)} - {formatHHmm(program.endTime)}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
